import type { PersonTaskStats } from '../../lib/peopleApi';
import { statusBadgeStyle } from '../tasks/taskConstants';
import { useTaskStatuses } from '../../hooks/useTaskStatuses';

interface Props {
  stats: PersonTaskStats;
}

function StatCard({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className={`text-2xl font-bold ${accent ?? 'text-gray-900'}`}>{value}</div>
      <div className="mt-1 text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
    </div>
  );
}

/**
 * Summary stats block for the People detail page (CLE-34): headline counters
 * plus a per-status breakdown rendered as labelled count badges. Statuses are
 * configurable — byStatus is keyed by slug and ordered by the board order.
 */
export function PersonTaskStatsCards({ stats }: Props) {
  const { statuses } = useTaskStatuses();
  const statusEntries = statuses
    .filter((s) => (stats.byStatus[s.slug] ?? 0) > 0)
    .map((s) => ({ status: s, count: stats.byStatus[s.slug] ?? 0 }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Sarcini active" value={stats.totalActive} />
        <StatCard label="Întârziate" value={stats.overdue} accent="text-red-600" />
        <StatCard label="În lucru" value={stats.inProgress} accent="text-blue-600" />
        <StatCard label="Proiecte active" value={stats.activeProjects} />
      </div>

      {statusEntries.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
            După status
          </span>
          {statusEntries.map(({ status, count }) => (
            <span
              key={status.id}
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium"
              style={statusBadgeStyle(status.color)}
            >
              {status.name}
              <span className="font-semibold">{count}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
